import React from "react";
import { motion } from "framer-motion";
import { TrendingUp } from "lucide-react";
import { SectionLabel } from "./SectionLabel";
import { PILLARS, fadeUp, stagger } from "./constants";

const PillarSection = ({ pillar, index, isReversed }) => (
  <motion.div {...stagger(0)} data-testid={`pillar-detail-${index}`}
    className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${isReversed ? "lg:direction-rtl" : ""}`}>
    <div className={isReversed ? "lg:order-2" : ""}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${pillar.accent}20`, border: `1px solid ${pillar.accent}40` }}>
          <pillar.icon size={22} style={{ color: pillar.accent }} />
        </div>
        <div>
          <h3 className="font-serif text-2xl text-[#F7F5F0]">{pillar.title}</h3>
          {pillar.maleNote && <p className="text-xs text-[#A3B8AD] mt-0.5">{pillar.maleNote}</p>}
        </div>
      </div>
      <p className="text-[#D4AF37] text-sm font-medium mb-4 leading-relaxed">{pillar.summary}</p>
      <p className="text-[#F7F5F0]/70 text-sm leading-[1.8] mb-6">{pillar.description}</p>
      <div className="space-y-3">
        {pillar.outcomes.map((o) => (
          <div key={o.text} className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <o.icon size={14} className="text-[#D4AF37]" />
            </div>
            <span className="text-sm text-[#F7F5F0]/80 leading-relaxed">{o.text}</span>
          </div>
        ))}
      </div>
    </div>
    <div className={`${isReversed ? "lg:order-1" : ""} flex justify-center`}>
      <div className="relative">
        <div className="w-48 h-48 md:w-56 md:h-56 rounded-3xl flex flex-col items-center justify-center text-center"
          style={{ backgroundColor: `${pillar.accent}12`, border: `1px solid ${pillar.accent}25` }}>
          <span className="font-serif text-5xl md:text-6xl text-[#F7F5F0]">{pillar.stat.value}</span>
          <span className="text-xs text-[#A3B8AD] mt-2 px-6 leading-relaxed">{pillar.stat.label}</span>
        </div>
        <div className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-[#D4AF37]/20 border border-[#D4AF37]/30" />
        <div className="absolute -bottom-2 -left-2 w-5 h-5 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20" />
      </div>
    </div>
  </motion.div>
);

export default function ElitePillars() {
  return (
    <>
      {/* Five Pillars */}
      <section id="method" className="py-24 md:py-32 bg-[#0D1F17] relative overflow-hidden" data-testid="five-pillars">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E\")" }} />
        <div className="max-w-6xl mx-auto px-6 lg:px-8 relative z-10">
          <motion.div {...fadeUp} className="text-center mb-20">
            <SectionLabel light>The Fitly Precision Fertility Protocol</SectionLabel>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#F7F5F0] mt-4">
              Five Systems, <span className="italic font-light text-[#D4AF37]">One Cycle</span>
            </h2>
            <p className="text-[#A3B8AD] mt-4 text-sm max-w-lg mx-auto">
              Each pillar is clinically informed and interconnected — addressing the root causes of fertility challenges, not just symptoms.
            </p>
          </motion.div>
          <div className="space-y-24 md:space-y-32">
            {PILLARS.map((pillar, i) => (
              <PillarSection key={pillar.title} pillar={pillar} index={i} isReversed={i % 2 !== 0} />
            ))}
          </div>
        </div>
      </section>

      {/* GLP-1 Supportive Nutrition Note */}
      <section className="py-20 md:py-24 bg-[#FEFDFB] text-[#0D1F17]" data-testid="glp1-note">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp}
            className="relative bg-white border border-[#0D1F17]/6 rounded-3xl p-8 md:p-12 shadow-sm overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#A8D5BA] via-[#D4AF37] to-[#E8B4B8]" />
            <div className="flex items-start gap-5">
              <div className="w-12 h-12 rounded-xl bg-[#A8D5BA]/15 border border-[#A8D5BA]/25 flex items-center justify-center flex-shrink-0 mt-1">
                <TrendingUp size={22} className="text-[#A8D5BA]" />
              </div>
              <div>
                <span className="text-[10px] tracking-[0.25em] uppercase font-semibold text-[#D4AF37]">A Note on Natural Satiety Support</span>
                <h3 className="font-serif text-xl md:text-2xl text-[#0D1F17] mt-2 mb-4">GLP-1-Supportive Nutrition</h3>
                <p className="text-sm text-[#0D1F17]/70 leading-[1.8] mb-4">
                  Several of our weekly focus areas — protein adequacy, healthy fats, omega-3s, and fiber &amp; gut health — are also the foods shown to naturally support the body's own GLP-1 (satiety hormone) response. Alongside meal sequencing (protein and fat before carbohydrates) and stress management, these pillars work together to regulate blood sugar and appetite naturally.
                </p>
                <p className="text-xs text-[#0D1F17]/40 leading-relaxed italic">
                  This is food-based support for the body's natural satiety and blood sugar regulation — not a substitute for GLP-1 medication.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
